import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {useBrowserHeight} from '~/common/hooks/useBrowserHeight';
import {useBrowserWidth} from '~/common/hooks/useBrowserWidth';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {useIsMediumAndUp} from '~/common/hooks/useMatchQuery';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';
import {MaskingFilter} from '~/GalleryPB/components/MaskingFilter/MaskingFilter';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import linerShading from '~images/liner_shading.png';
import {Video} from '~library/atoms/Video';

import './PremiumAnimationB.sass';

export const PremiumAnimationB = ({
  template,
  templateSrc,
  skipAnimation,
  liner,
  envelopeClosedBack,
  envelopeOpen,
}) => {
  const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
  const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);
  const hasMedia = !!media;
  const isMediumScreenAndUp = useIsMediumAndUp();
  const {isInUyoOrDyo} = useUyoOrDyo();

  const inviteContainerRef = React.useRef(null);
  const envelopeOpenFlapRef = React.useRef(null);
  const envelopeClosedFlapRef = React.useRef(null);
  const envelopeRef = React.useRef(null);
  const linerRef = React.useRef(null);
  const linerShadingRef = React.useRef(null);
  const yourInvitationRef = React.useRef(null);
  const templateRef = React.useRef(null);

  const browserWidth = useBrowserWidth();
  const browserHeight = useBrowserHeight();

  const [envelopeLoaded, setEnvelopeLoaded] = React.useState(false);

  const isTemplateSrcAVideo = templateSrc.endsWith('mp4');
  const templateDefault = isMediumScreenAndUp ? '250x340_uyo_mask' : '500x680_uyo_mask';

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  React.useEffect(() => {
    const envelopeAnimation = () => {
      window.setTimeout(() => {
        inviteContainerRef?.current?.classList.remove('step-0');
        inviteContainerRef?.current?.classList.add('step-1');

        window.setTimeout(() => {
          inviteContainerRef?.current?.classList.remove('step-1');
          inviteContainerRef?.current?.classList.add('step-2');

          window.setTimeout(() => {
            inviteContainerRef?.current?.classList.remove('step-2');
            inviteContainerRef?.current?.classList.add('step-3');

            window.setTimeout(() => {
              inviteContainerRef?.current?.classList.remove('step-3');
              inviteContainerRef?.current?.classList.add('step-4');

              window.setTimeout(() => {
                inviteContainerRef?.current?.classList.remove('step-4');
                inviteContainerRef?.current?.classList.add('step-0');
              }, 460);
            }, 1960);
          }, 460);
        }, 460);
      }, 920);
    };
    envelopeAnimation();
    const interval = setInterval(() => {
      envelopeAnimation();
    }, 5600);
    return () => {
      clearInterval(interval);
    };
  }, [envelopeLoaded, skipAnimation]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  React.useEffect(() => {
    if (yourInvitationRef.current) {
      const width = yourInvitationRef.current.offsetHeight / 1.36;
      templateRef.current.style.width = `${width}px`;
      templateRef.current.style.right = `calc((100% - ${width}px) / 2)`;
      envelopeRef.current.style.width = `${width}px`;
      envelopeRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      envelopeOpenFlapRef.current.style.width = `${width}px`;
      envelopeOpenFlapRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      envelopeClosedFlapRef.current.style.width = `${width}px`;
      envelopeClosedFlapRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      linerRef.current.style.width = `${width}px`;
      linerRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      linerShadingRef.current.style.width = `${width}px`;
      linerShadingRef.current.style.left = `calc((100% - ${width}px) / 2)`;
    }
  }, [browserWidth, browserHeight]);

  return (
    <div
      className="invite-container variant-B step-0 fabric" // TODO: Rebuild animation
      ref={inviteContainerRef}
    >
      <div className="invite-animation open-flap">
        <div
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__open-flap-group`}
          ref={envelopeOpenFlapRef}
        >
          <img
            className="invite-envelope__open-flap invite-envelope__open-flap-shading"
            src={linerShading}
            alt=""
          />
          <img className="invite-envelope__open-flap" src={envelopeOpen} alt="" />
          <img className="invite-envelope__top-liner" src={liner} alt="" />
        </div>
      </div>

      <div className="invite-animation open-bottom">
        <img
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__bottom-liner`}
          src={liner}
          ref={linerRef}
          alt=""
        />
        <img
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__bottom-liner-shading`}
          src={linerShading}
          ref={linerShadingRef}
          alt=""
        />
      </div>

      <div className="invite-animation your-invitation" ref={yourInvitationRef}>
        <div
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__your-invitation fabric`}
        >
          <div className="invite-envelope__your-invitation__card" ref={templateRef}>
            {isTemplateSrcAVideo && (
              <Video
                src={templateSrc}
                className={`fabric_user_card ${envelopeLoaded ? 'ready' : ''}`}
                alt=""
              />
            )}
            {!isTemplateSrcAVideo &&
              (hasMedia && isInUyoOrDyo && (template.dyo || template.uyo)
                ? <MaskingFilter
                    media={media}
                    templateDefault={template?.assets?.default[templateDefault]}
                    uyoPosition={template.uyoImagePosition}
                    isMobile={!isMediumScreenAndUp}
                  />
                : <img
                    src={templateSrc}
                    className={cx('fabric_user_card', {ready: envelopeLoaded})}
                    alt=""
                  />)}
          </div>
        </div>
      </div>

      <div className="invite-animation open-bottom-front">
        <img
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__open-bottom-front`}
          src={envelopeOpen}
          ref={envelopeRef}
          onLoad={() => setEnvelopeLoaded(true)}
          alt=""
        />
      </div>

      <div className="invite-animation closed-flap">
        <img
          className={`animation-image ${
            envelopeLoaded ? 'ready' : ''
          } invite-envelope__closed-flap`}
          src={envelopeClosedBack}
          ref={envelopeClosedFlapRef}
          alt=""
        />
      </div>
    </div>
  );
};

PremiumAnimationB.propTypes = {
  liner: PropTypes.string,
  envelopeClosedBack: PropTypes.string,
  envelopeOpen: PropTypes.string,
  templateSrc: PropTypes.string.isRequired,
};
